/* eslint-disable max-len */
import React from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Typography
} from '@mui/material';
import {
    Send,
    LocalShipping,
    Construction,
    CardMembership,
    Forest
} from '@mui/icons-material';

import { MissingRequirementsType } from '../../types';
import { selectUserProfile } from '../../redux/actions/users';
import toastMessage from '../../utils/toast';
import OfferChatMessage from './OfferChatMessage';
import { useDispatch, useSelector } from '../../redux/hooks';
import { updateTenantMetadata } from '../../api/tenants';
import {
    fetchOfferMessagesById,
    selectOfferMessagesById
} from '../../redux/actions/offers';
import { createOfferMessage } from '../../api/offers';
import { InfoText } from '../CommonComponents';
import { createNewOfferMessage } from '../../api/jobs';
import { defaultMissingRequirements } from '../../utils/metadata';

type PropsType = {
    recipient?: string | null,
    id?: number | null,
    orderId?: number | null,
    canWrite?: boolean,
    maxHeight?: string,
    missingRequirements?: MissingRequirementsType;
}

const FETCH_INTERVAL = 60 * 1000; // 60 seconds

export default function OfferChat({
    recipient = 'Motpart',
    id,
    orderId,
    canWrite,
    maxHeight,
    missingRequirements = defaultMissingRequirements
}: PropsType) {
    const dispatch = useDispatch();
    const userProfile = useSelector(selectUserProfile());
    const [newOfferId, setNewOfferId] = React.useState<number | null>(null);
    const [isSending, setIsSending] = React.useState(false);

    const newMessageRef = React.useRef<HTMLInputElement>();
    const messageContainerRef = React.useRef<HTMLDivElement>();

    const targetOfferId = id || newOfferId;
    const messages = useSelector(selectOfferMessagesById(targetOfferId));
    const [chatButtonOverride, setChatButtonOverride] = React.useState<null | boolean>(null);

    const chatButtonDisabled = React.useMemo(() => {
        if (chatButtonOverride !== null) return !chatButtonOverride;
        return messages?.length === 0 && missingRequirements.isMissingRequirements;
    }, [messages, missingRequirements, chatButtonOverride]);

    React.useEffect(() => {
        if (targetOfferId) {
            dispatch(fetchOfferMessagesById(targetOfferId));

            const intervalId = window.setInterval(() => {
                dispatch(fetchOfferMessagesById(targetOfferId));
            }, FETCH_INTERVAL);

            return () => window.clearInterval(intervalId); // Runs before every effect-run, and on unmount
        }

        return () => { };
    }, [dispatch, id, targetOfferId]);

    React.useEffect(() => {
        if (messageContainerRef.current) {
            messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
        }
    }, [messages?.length]);

    const sendMessage = React.useCallback(async () => {
        const message = newMessageRef.current?.value;

        if (!message || (!id && !orderId)) {
            return;
        }
        try {
            setIsSending(true);
            if (id) {
                await createOfferMessage({
                    id,
                    message
                });
                dispatch(fetchOfferMessagesById(id));
            } else if (orderId) {
                const { offerId: newId } = await createNewOfferMessage({
                    orderId,
                    message
                });
                setNewOfferId((prevId) => prevId || newId);
            }
            if (newMessageRef.current) {
                newMessageRef.current.value = '';
                newMessageRef.current?.focus();
            }
        } catch (e) {
            // Fixme: Must handle errors better here
            toastMessage(e, 'Kunde inte skicka meddelande, försök igen');
        } finally {
            setIsSending(false);
        }
    }, [dispatch, id, orderId]);

    const onKeyDown = React.useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    }, [sendMessage]);

    const handleSenderIsQualified = React.useCallback(async () => {
        try {
            await updateTenantMetadata({
                vehicleTypes: missingRequirements.missingVehicleTypes,
                equipment: missingRequirements.missingEquipment,
                sustainability: missingRequirements.missingSustainability,
                licencesCertificates: missingRequirements.missingLicencesCertificates,
                customLicencesCertificates: missingRequirements.missingCustomLicencesCertificates,
            });
            setChatButtonOverride(true);
            toastMessage('Företagsuppgifter uppdaterade');
        } catch (e: any) {
            toastMessage(e, 'Kunde inte uppdatera företagsuppgifterna, försök igen');
        }
    }, [missingRequirements]);

    const missingCount = missingRequirements.missingVehicleTypes.length
        + missingRequirements.missingEquipment.length
        + missingRequirements.missingSustainability.length
        + missingRequirements.missingLicencesCertificates.length
        + missingRequirements.missingCustomLicencesCertificates.length;

    const paddingCount = missingCount % 3 === 0 ? 0 : 3 - (missingCount % 3);
    const paddingSlots = React.useMemo(() => ['pad-1', 'pad-2'], []);

    return (
        <>
            <Box
                ref={messageContainerRef}
                sx={{ maxHeight, overflowY: 'auto', marginBottom: 1 }}
            >
                {canWrite && messages && messages.length === 0 && !chatButtonDisabled && (
                    <InfoText>
                        Meddelanden du skickar är synliga för samtliga
                        användare i både ditt och motpartens företag.
                    </InfoText>
                )}
                {chatButtonDisabled && (
                    <>
                        {missingRequirements.missingFairTransport && (
                            <Alert
                                severity="warning"
                                sx={{
                                    mb: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1
                                }}
                            >
                                Uppdraget kräver certifiering av Fair Transport. Detta är inget du kan ändra själv, utan utgår från verifierad data. Är du certifierad - kontakta då vår support så hjälper vi dig.
                            </Alert>
                        )}
                        <Alert
                            severity="info"
                            sx={{
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1
                            }}
                        >
                            {`Uppdraget kräver ${missingRequirements.missingFairTransport
                                ? ' dessutom '
                                : ''}
                                ${missingCount === 1 ? 'en resurs' : `${missingCount} resurser`} som ni inte har angett i er företagsprofil`}
                            {`${!userProfile?.isAdmin
                                ? '. Om detta inte stämmer, kontakta din administratör för att uppdatera företagets profil.'
                                : ':'}`}
                        </Alert>
                        <Grid
                            container
                            sx={{
                                px: 3,
                                pt: 3,
                                pb: 2,
                                border: '1px solid',
                                borderColor: 'grey.300',
                                borderRadius: 1,
                                mb: 1
                            }}
                        >
                            <Grid item xs={12}>
                                <Grid
                                    container
                                    rowSpacing={1}
                                    columnSpacing={1}
                                    justifyContent="space-evenly"
                                    alignItems="flex-start"
                                >
                                    {missingRequirements.missingVehicleTypes.map((veh: string) => (
                                        <Grid
                                            item
                                            xs={4}
                                            key={veh}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'center',
                                                alignItems: 'stretch'
                                            }}
                                        >
                                            <LocalShipping fontSize="small" color="inherit" />
                                            <Typography variant="body1" sx={{ ml: 0.5, mt: -0.2 }}>
                                                {veh}
                                            </Typography>
                                        </Grid>
                                    ))}

                                    {missingRequirements.missingEquipment.map((eq: string) => (
                                        <Grid
                                            item
                                            xs={4}
                                            key={eq}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'center',
                                                alignItems: 'stretch'
                                            }}
                                        >
                                            <Construction fontSize="small" color="inherit" />
                                            <Typography variant="body1" sx={{ ml: 0.5, mt: -0.2 }}>
                                                {eq}
                                            </Typography>
                                        </Grid>
                                    ))}

                                    {missingRequirements.missingSustainability.map((sus: string) => (
                                        <Grid
                                            item
                                            xs={4}
                                            key={sus}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'center',
                                                alignItems: 'stretch'
                                            }}
                                        >
                                            <Forest fontSize="small" color="inherit" />
                                            <Typography variant="body1" sx={{ ml: 0.5, mt: -0.2 }}>
                                                {sus}
                                            </Typography>
                                        </Grid>
                                    ))}

                                    {[
                                        ...missingRequirements.missingLicencesCertificates,
                                        ...missingRequirements.missingCustomLicencesCertificates
                                    ].map((lic: string) => (
                                        <Grid
                                            item
                                            xs={4}
                                            key={lic}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'center',
                                                alignItems: 'stretch',
                                            }}
                                        >
                                            <CardMembership fontSize="small" color="inherit" />
                                            <Typography variant="body1" sx={{ ml: 0.5, mt: -0.2 }}>
                                                {lic}
                                            </Typography>
                                        </Grid>
                                    ))}

                                    {paddingSlots.slice(0, paddingCount).map((slotId) => (
                                        <Grid item xs={4} key={slotId} />
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container textAlign="center" mt={-1} mb={2}>
                            {userProfile?.isAdmin && (
                                <>
                                    <Grid item xs={12}>
                                        <Typography variant="h6" mt={3} mb={2}>
                                            {`Uppfyller ditt företag ${missingCount === 1
                                                ? 'detta krav?'
                                                : `dessa ${missingCount} krav?`}`}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} mt={1}>
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            onClick={() => handleSenderIsQualified()}
                                            disabled={!chatButtonDisabled}
                                        >
                                            Ja, vi uppfyller kraven
                                        </Button>
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={userProfile?.isAdmin ? 6 : 12} mt={userProfile?.isAdmin ? 1 : 3}>
                                <Button
                                    color="primary"
                                    variant={userProfile?.isAdmin ? 'outlined' : 'contained'}
                                    onClick={() => setChatButtonOverride(true)}
                                    disabled={!chatButtonDisabled}
                                >
                                    {userProfile?.isAdmin ? 'Nej, skicka prisförslag ändå' : 'Skicka prisförslag ändå'}
                                </Button>
                            </Grid>
                        </Grid>

                    </>
                )}
                {messages && messages.map((message) => (
                    <OfferChatMessage
                        key={message.id}
                        message={message}
                    />
                ))}
            </Box>

            {canWrite ? (
                <div style={{ display: `${!chatButtonDisabled ? 'flex' : 'none'}`, alignItems: 'center' }}>
                    <OutlinedInput
                        id="new-message"
                        inputRef={newMessageRef}
                        disabled={isSending || chatButtonDisabled}
                        fullWidth
                        multiline
                        color="primary"
                        placeholder={`Skriv något till ${recipient}`}
                        endAdornment={(
                            <InputAdornment position="end">
                                {isSending ? <CircularProgress size={30} color="primary" /> : (
                                    <IconButton
                                        aria-label="Skicka"
                                        color="primary"
                                        onClick={sendMessage}
                                        edge="end"
                                        disabled={chatButtonDisabled}
                                    >
                                        <Send />
                                    </IconButton>
                                )}
                            </InputAdornment>
                        )}
                        onKeyDown={onKeyDown}
                    />
                </div>
            ) : (
                <InfoText showBorder>
                    Uppdraget är inte längre aktivt, så ni kan inte skriva nya meddelanden.
                </InfoText>
            )}
        </>
    );
}
